import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, Grid, Divider, CircularProgress } from '@mui/material';
import { Subcontractor } from '../../services/subcontractorService';
import subcontractorService from '../../services/subcontractorService';
import api from '../../services/api';
import { OfferStandaloneInquiryDTO } from '../../services/offerStandaloneManageService';

interface SubcontractorDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  contractorEmail: string;
  inquiry?: OfferStandaloneInquiryDTO | null;
  packageName?: string;
}

const fieldLabelStyle = { color: '#666', fontSize: '0.75rem', fontFamily: 'Roboto, sans-serif' } as const;
const fieldValueStyle = { fontWeight: 500, fontSize: '0.9rem', fontFamily: 'Roboto, sans-serif', color: '#2D2D32' } as const;

const SubcontractorDetailsDrawer: React.FC<SubcontractorDetailsDrawerProps> = ({ open, onClose, contractorEmail, inquiry, packageName }) => {
  const [subcontractor, setSubcontractor] = useState<Subcontractor | null>(null);
  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState<string>('');

  useEffect(() => {
    if (!open || !contractorEmail) {
      setSubcontractor(null);
      setProjectName('');
      return;
    }

    const findSubcontractorByEmail = async () => {
      setLoading(true);
      try {
        // Pobierz wszystkich podwykonawców (używamy endpointu który zwraca wszystkich)
        // Najpierw spróbujmy pobrać z wszystkich projektów
        // Musimy użyć endpointu który zwraca wszystkich podwykonawców
        const response = await api.get('/subcontractors');
        const allSubcontractors: Subcontractor[] = Array.isArray(response.data) ? response.data : [];
        
        // Znajdź podwykonawcę po emailu
        const found = allSubcontractors.find(
          (sub) => sub.email && sub.email.toLowerCase() === contractorEmail.toLowerCase()
        );

        if (found) {
          setSubcontractor(found);
          // Pobierz nazwę projektu jeśli mamy project_id
          if (found.project_id) {
            try {
              const projectResponse = await api.get(`/projects/${found.project_id}`);
              setProjectName(projectResponse.data?.project_name || '');
            } catch {
              // Ignoruj błąd pobierania projektu
            }
          }
        } else {
          setSubcontractor(null);
        }
      } catch (error) {
        console.error('Błąd podczas wyszukiwania podwykonawcy:', error);
        setSubcontractor(null);
      } finally {
        setLoading(false);
      }
    };

    findSubcontractorByEmail();
  }, [open, contractorEmail]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Podwykonawca
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ px: 4, pt: '36px', pb: 4, flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !subcontractor ? (
            <Box>
              {inquiry ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#666', fontSize: '0.75rem', mb: 1 }}>
                      Nie znaleziono podwykonawcy w bazie. Dane z zapytania:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={fieldLabelStyle}>E-mail</Typography>
                    <Typography sx={fieldValueStyle}>{inquiry.contractor_email || '-'}</Typography>
                  </Grid>
                  {packageName && (
                    <Grid item xs={12} md={6}>
                      <Typography sx={fieldLabelStyle}>Pakiet</Typography>
                      <Typography sx={fieldValueStyle}>{packageName}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography sx={fieldLabelStyle}>Telefon nr 1</Typography>
                    <Typography sx={fieldValueStyle}>{inquiry.contractor_phone_1 || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={fieldLabelStyle}>Telefon nr 2</Typography>
                    <Typography sx={fieldValueStyle}>{inquiry.contractor_phone_2 || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={fieldLabelStyle}>Temat</Typography>
                    <Typography sx={fieldValueStyle}>{inquiry.subject || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={fieldLabelStyle}>Status</Typography>
                    <Typography sx={fieldValueStyle}>{inquiry.status || '-'}</Typography>
                  </Grid>
                  {inquiry.created_at && (
                    <Grid item xs={12} md={6}>
                      <Typography sx={fieldLabelStyle}>Data utworzenia</Typography>
                      <Typography sx={fieldValueStyle}>
                        {new Date(inquiry.created_at).toLocaleDateString('pl-PL')}
                      </Typography>
                    </Grid>
                  )}
                  {inquiry.inquiry_id && (
                    <Grid item xs={12}>
                      <Typography sx={fieldLabelStyle}>ID zapytania</Typography>
                      <Typography sx={fieldValueStyle}>{inquiry.inquiry_id}</Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#2D2D32', mb: 2 }}>
                    Nie znaleziono podwykonawcy dla adresu e-mail:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, color: '#2D2D32' }}>
                    {contractorEmail}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelStyle}>Numer umowy</Typography>
                  <Typography sx={fieldValueStyle}>{subcontractor.contract_number}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelStyle}>TWU</Typography>
                  <Typography sx={fieldValueStyle}>{subcontractor.completion_date || '-'}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelStyle}>Nazwa podwykonawcy</Typography>
                  <Typography sx={fieldValueStyle}>{subcontractor.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelStyle}>Wartość umowy</Typography>
                  <Typography sx={fieldValueStyle}>
                    {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(subcontractor.contract_value)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelStyle}>E-mail</Typography>
                  <Typography sx={fieldValueStyle}>{subcontractor.email || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={fieldLabelStyle}>Telefon</Typography>
                  <Typography sx={fieldValueStyle}>{subcontractor.phone || '-'}</Typography>
                </Grid>

                {projectName && (
                  <Grid item xs={12}>
                    <Typography sx={fieldLabelStyle}>Projekt</Typography>
                    <Typography sx={fieldValueStyle}>{projectName}</Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={fieldLabelStyle}>Przedmiot umowy</Typography>
                  <Typography sx={{ ...fieldValueStyle, whiteSpace: 'pre-wrap' }}>
                    {subcontractor.contract_subject}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SubcontractorDetailsDrawer;
